// Shopify Product Comparison Feature
// This code adds a comparison functionality to product pages

// Configuration
const compareConfig = {
  maxCompareItems: 4,              // Maximum number of products to compare
  storageKey: 'shopify_compare',   // Local storage key
  compareButtonText: 'Compare',    // Text for the compare button
  removeText: 'Remove',            // Text for remove button
  compareNowText: 'Compare Now',   // Text for compare now button
  emptyCompareText: 'No products selected for comparison',
  compareUrl: '/pages/compare'     // URL for dedicated compare page (optional)
};

// Main class for product comparison functionality
class ProductCompare {
  constructor(config) {
    this.config = config;
    this.compareItems = this.getCompareItems();
    this.init();
  }

  // Initialize the comparison functionality
  init() {
    this.addCompareButtons();
    this.updateCompareCounter();
    this.setupEventListeners();
    
    // If we're on the comparison page, render the comparison table
    if (window.location.pathname === this.config.compareUrl) {
      this.renderComparisonTable();
    }
  }

  // Add compare buttons to all product cards in the collection
  addCompareButtons() {
    // Find all product cards on the page
    const productCards = document.querySelectorAll('.product-card, .product, .grid__item');
    
    productCards.forEach(card => {
      const productId = card.getAttribute('data-product-id');
      if (!productId) return;
      
      // Check if button already exists
      if (card.querySelector('.compare-button')) return;
      
      // Create compare button
      const compareButton = document.createElement('button');
      compareButton.classList.add('compare-button');
      compareButton.setAttribute('data-product-id', productId);
      compareButton.textContent = this.isInCompare(productId) ? this.config.removeText : this.config.compareButtonText;
      
      // Add active class if product is in compare list
      if (this.isInCompare(productId)) {
        compareButton.classList.add('active');
      }
      
      // Add button to card
      card.querySelector('.product-card__info, .product-info, .grid-view-item').appendChild(compareButton);
    });

    // Also add compare button to the product page if we're on one
    const productPage = document.querySelector('.product');
    if (productPage) {
      const productId = productPage.getAttribute('data-product-id');
      if (productId) {
        const productForm = document.querySelector('.product-form, .product-form__controls-group');
        if (productForm && !productForm.querySelector('.compare-button')) {
          const compareButton = document.createElement('button');
          compareButton.classList.add('compare-button', 'product-page-compare');
          compareButton.setAttribute('data-product-id', productId);
          compareButton.textContent = this.isInCompare(productId) ? this.config.removeText : this.config.compareButtonText;
          
          if (this.isInCompare(productId)) {
            compareButton.classList.add('active');
          }
          
          productForm.appendChild(compareButton);
        }
      }
    }
  }

  // Setup event listeners for compare buttons
  setupEventListeners() {
    // Listen for clicks on compare buttons
    document.addEventListener('click', event => {
      if (event.target.classList.contains('compare-button')) {
        event.preventDefault();
        const productId = event.target.getAttribute('data-product-id');
        this.toggleCompareItem(productId, event.target);
      }
      
      // Compare now button
      if (event.target.id === 'compare-now-button') {
        event.preventDefault();
        this.showComparison();
      }
      
      // Close comparison overlay
      if (event.target.id === 'close-comparison') {
        event.preventDefault();
        this.closeComparison();
      }
    });
  }

  // Toggle a product in the comparison list
  toggleCompareItem(productId, buttonElement) {
    if (this.isInCompare(productId)) {
      // Remove from compare
      this.compareItems = this.compareItems.filter(item => item !== productId);
      buttonElement.textContent = this.config.compareButtonText;
      buttonElement.classList.remove('active');
    } else {
      // Add to compare if we haven't reached the maximum
      if (this.compareItems.length < this.config.maxCompareItems) {
        this.compareItems.push(productId);
        buttonElement.textContent = this.config.removeText;
        buttonElement.classList.add('active');
      } else {
        alert(`You can only compare up to ${this.config.maxCompareItems} products at once.`);
        return;
      }
    }
    
    // Update storage and UI
    this.saveCompareItems();
    this.updateCompareCounter();
    
    // Update all buttons for this product
    const allButtons = document.querySelectorAll(`.compare-button[data-product-id="${productId}"]`);
    allButtons.forEach(button => {
      button.textContent = this.isInCompare(productId) ? this.config.removeText : this.config.compareButtonText;
      if (this.isInCompare(productId)) {
        button.classList.add('active');
      } else {
        button.classList.remove('active');
      }
    });
  }

  // Check if a product is in the compare list
  isInCompare(productId) {
    return this.compareItems.includes(productId);
  }

  // Get compare items from local storage
  getCompareItems() {
    const stored = localStorage.getItem(this.config.storageKey);
    return stored ? JSON.parse(stored) : [];
  }

  // Save compare items to local storage
  saveCompareItems() {
    localStorage.setItem(this.config.storageKey, JSON.stringify(this.compareItems));
  }

  // Update the compare counter in the header
  updateCompareCounter() {
    let compareCounter = document.getElementById('compare-counter');
    
    if (!compareCounter) {
      // Create the compare counter if it doesn't exist
      const header = document.querySelector('.site-header, header');
      if (header) {
        const compareContainer = document.createElement('div');
        compareContainer.classList.add('compare-container');
        
        compareCounter = document.createElement('span');
        compareCounter.id = 'compare-counter';
        compareCounter.classList.add('compare-counter');
        
        const compareNowButton = document.createElement('button');
        compareNowButton.id = 'compare-now-button';
        compareNowButton.classList.add('compare-now-button');
        compareNowButton.textContent = this.config.compareNowText;
        
        compareContainer.appendChild(compareCounter);
        compareContainer.appendChild(compareNowButton);
        
        // Insert before the cart
        const cart = header.querySelector('.cart-link, .site-header__cart');
        if (cart) {
          header.insertBefore(compareContainer, cart);
        } else {
          header.appendChild(compareContainer);
        }
      }
    }
    
    if (compareCounter) {
      compareCounter.textContent = this.compareItems.length;
      compareCounter.style.display = this.compareItems.length > 0 ? 'inline-block' : 'none';
      
      // Also update the compare now button visibility
      const compareNowButton = document.getElementById('compare-now-button');
      if (compareNowButton) {
        compareNowButton.style.display = this.compareItems.length > 0 ? 'inline-block' : 'none';
      }
    }
  }
  
  // Show comparison overlay or redirect to compare page
  showComparison() {
    if (this.compareItems.length === 0) {
      alert(this.config.emptyCompareText);
      return;
    }
    
    if (this.config.compareUrl) {
      // Redirect to dedicated compare page
      window.location.href = this.config.compareUrl;
    } else {
      // Create and show comparison overlay
      this.createComparisonOverlay();
    }
  }
  
  // Create an overlay to show product comparison
  createComparisonOverlay() {
    // Remove existing overlay if any
    const existingOverlay = document.getElementById('comparison-overlay');
    if (existingOverlay) {
      existingOverlay.remove();
    }
    
    // Create overlay container
    const overlay = document.createElement('div');
    overlay.id = 'comparison-overlay';
    overlay.classList.add('comparison-overlay');
    
    // Create close button
    const closeButton = document.createElement('button');
    closeButton.id = 'close-comparison';
    closeButton.classList.add('close-comparison');
    closeButton.innerHTML = '&times;';
    
    // Create comparison content
    const comparisonContent = document.createElement('div');
    comparisonContent.classList.add('comparison-content');
    
    // Load products and create comparison table
    this.fetchCompareProducts().then(products => {
      if (products.length === 0) {
        comparisonContent.innerHTML = '<p>No products found for comparison.</p>';
        return;
      }
      
      // Create comparison table
      const table = this.createComparisonTable(products);
      comparisonContent.appendChild(table);
    });
    
    // Assemble overlay
    overlay.appendChild(closeButton);
    overlay.appendChild(comparisonContent);
    document.body.appendChild(overlay);
    
    // Prevent page scrolling when overlay is open
    document.body.style.overflow = 'hidden';
  }
  
  // Close the comparison overlay
  closeComparison() {
    const overlay = document.getElementById('comparison-overlay');
    if (overlay) {
      overlay.remove();
      document.body.style.overflow = '';
    }
  }
  
  // Fetch product data for products in compare list
  async fetchCompareProducts() {
    const products = [];
    
    for (const productId of this.compareItems) {
      try {
        const response = await fetch(`/products/${productId}.js`);
        if (response.ok) {
          const product = await response.json();
          products.push(product);
        }
      } catch (error) {
        console.error(`Error fetching product ${productId}:`, error);
      }
    }
    
    return products;
  }
  
  // Create a comparison table from product data
  createComparisonTable(products) {
    const table = document.createElement('table');
    table.classList.add('comparison-table');
    
    // Create header row with product images and titles
    const headerRow = document.createElement('tr');
    headerRow.appendChild(document.createElement('th')); // Empty cell for feature names
    
    products.forEach(product => {
      const headerCell = document.createElement('th');
      
      // Product image
      const image = document.createElement('img');
      image.src = product.featured_image ? product.featured_image : product.images[0];
      image.alt = product.title;
      image.classList.add('compare-product-image');
      
      // Product title
      const title = document.createElement('h3');
      title.textContent = product.title;
      title.classList.add('compare-product-title');
      
      // Price
      const price = document.createElement('p');
      price.textContent = (product.price / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD'
      });
      price.classList.add('compare-product-price');
      
      // View product link
      const viewLink = document.createElement('a');
      viewLink.href = `/products/${product.handle}`;
      viewLink.textContent = 'View Product';
      viewLink.classList.add('compare-view-link');
      
      // Remove from comparison button
      const removeButton = document.createElement('button');
      removeButton.classList.add('compare-button', 'active');
      removeButton.setAttribute('data-product-id', product.id);
      removeButton.textContent = this.config.removeText;
      
      // Assemble header cell
      headerCell.appendChild(image);
      headerCell.appendChild(title);
      headerCell.appendChild(price);
      headerCell.appendChild(viewLink);
      headerCell.appendChild(removeButton);
      
      headerRow.appendChild(headerCell);
    });
    
    table.appendChild(headerRow);
    
    // Common product features to compare
    const features = [
      { name: 'Vendor', key: 'vendor' },
      { name: 'Type', key: 'product_type' },
      { name: 'Availability', key: 'available', formatter: val => val ? 'In stock' : 'Out of stock' }
    ];
    
    // Add rows for each feature
    features.forEach(feature => {
      const row = document.createElement('tr');
      
      // Feature name cell
      const featureCell = document.createElement('td');
      featureCell.textContent = feature.name;
      featureCell.classList.add('feature-name');
      row.appendChild(featureCell);
      
      // Value for each product
      products.forEach(product => {
        const valueCell = document.createElement('td');
        let value = product[feature.key];
        
        // Apply formatter if provided
        if (feature.formatter) {
          value = feature.formatter(value);
        }
        
        valueCell.textContent = value;
        row.appendChild(valueCell);
      });
      
      table.appendChild(row);
    });
    
    // Add product description row
    const descRow = document.createElement('tr');
    const descNameCell = document.createElement('td');
    descNameCell.textContent = 'Description';
    descNameCell.classList.add('feature-name');
    descRow.appendChild(descNameCell);
    
    products.forEach(product => {
      const descCell = document.createElement('td');
      descCell.innerHTML = product.description ? product.description.substring(0, 150) + '...' : 'No description available';
      descRow.appendChild(descCell);
    });
    
    table.appendChild(descRow);
    
    return table;
  }
  
  // Render comparison table on the dedicated compare page
  renderComparisonTable() {
    const compareContainer = document.getElementById('product-comparison-container');
    if (!compareContainer) return;
    
    if (this.compareItems.length === 0) {
      compareContainer.innerHTML = `<p>${this.config.emptyCompareText}</p>`;
      return;
    }
    
    this.fetchCompareProducts().then(products => {
      if (products.length === 0) {
        compareContainer.innerHTML = '<p>No products found for comparison.</p>';
        return;
      }
      
      // Create comparison table
      const table = this.createComparisonTable(products);
      compareContainer.innerHTML = '';
      compareContainer.appendChild(table);
    });
  }
}

// Initialize comparison functionality when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const productCompare = new ProductCompare(compareConfig);
  
  // Re-initialize after Shopify section updates
  if (Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => {
      productCompare.addCompareButtons();
    });
  }
});

// Add styles for the comparison feature
const compareStyles = document.createElement('style');
compareStyles.textContent = `
  .compare-button {
    background-color: #f5f5f5;
    border: 1px solid #ddd;
    padding: 5px 10px;
    margin-top: 10px;
    cursor: pointer;
    font-size: 12px;
    transition: all 0.3s;
  }
  
  .compare-button.active {
    background-color: #333;
    color: white;
  }
  
  .compare-container {
    position: relative;
    display: inline-block;
    margin-right: 15px;
  }
  
  .compare-counter {
    background-color: #ff6b6b;
    color: white;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    display: inline-block;
    text-align: center;
    line-height: 20px;
    font-size: 12px;
    margin-right: 5px;
  }
  
  .compare-now-button {
    background-color: #4CAF50;
    color: white;
    border: none;
    padding: 5px 10px;
    cursor: pointer;
    font-size: 12px;
  }
  
  .comparison-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0,0,0,0.8);
    z-index: 9999;
    overflow-y: auto;
    padding: 20px;
  }
  
  .close-comparison {
    position: absolute;
    top: 10px;
    right: 10px;
    font-size: 30px;
    color: white;
    background: none;
    border: none;
    cursor: pointer;
  }
  
  .comparison-content {
    background-color: white;
    max-width: 1200px;
    margin: 50px auto;
    padding: 20px;
    border-radius: 5px;
  }
  
  .comparison-table {
    width: 100%;
    border-collapse: collapse;
  }
  
  .comparison-table th, .comparison-table td {
    padding: 10px;
    border: 1px solid #ddd;
    text-align: center;
  }
  
  .comparison-table th {
    background-color: #f5f5f5;
    vertical-align: top;
    min-width: 200px;
  }
  
  .comparison-table .feature-name {
    text-align: left;
    font-weight: bold;
    background-color: #f9f9f9;
  }
  
  .compare-product-image {
    max-width: 100px;
    height: auto;
    margin: 0 auto 10px;
    display: block;
  }
  
  .compare-product-title {
    margin: 5px 0;
    font-size: 16px;
  }
  
  .compare-product-price {
    font-weight: bold;
    margin: 5px 0 10px;
  }
  
  .compare-view-link {
    display: inline-block;
    margin: 5px 0;
    color: #0066cc;
    text-decoration: underline;
  }
  
  .product-page-compare {
    margin-top: 20px;
    padding: 10px 15px;
    font-size: 14px;
  }
  
  @media (max-width: 768px) {
    .comparison-table {
      font-size: 12px;
    }
    
    .comparison-table th {
      min-width: 120px;
    }
    
    .compare-product-image {
      max-width: 60px;
    }
    
    .compare-product-title {
      font-size: 14px;
    }
  }
`;

document.head.appendChild(compareStyles);